import { lagCsv } from '../lagCsv';
import { harTallakkorder } from '@/lib/sang/tallfil';
import { normaliserToneart } from '@/lib/sang/toneart';
import { tolkChordPro, tolkTekstfil, tonartFraFilnavn } from './tolkSangtekst';
import type { SangFelter } from './typer';

export interface LesSangfilerValg { maksFilstorrelse?: number; maksFiler?: number }
export interface Sang {
  filnavn: string;
  /** Slik filen ble lest. Tall-akkorder lagres som tall. */
  felter: SangFelter;
  /** toneart: bare tall-filer uten toneart fra filen eller filnavnet. artist: filen har ingen artist. */
  mangler: { toneart: boolean; artist: boolean };
  /** Hvor toneart kom fra for tall-filer, ellers null. */
  toneartKilde: 'fil' | 'filnavn' | null;
}
export type LesSangfilerResultat = { sanger: Sang[]; filadvarsler: string[] } | { feil: { melding: string }[] };
export type SangSvar = Record<string, { toneart?: string; artist?: string }>;
export interface SangerCsv { csv: string; filadvarsler: string[] }

const OVERSKRIFTER = ['Tittel', 'Artist', 'Album', 'Utgivelsesår', 'Lengde', 'Toneart', 'BPM', 'Takt', 'Kategorier', 'Lenker', 'Tekst og akkorder', 'Notater', 'Publisert'];
const ENDELSER = ['.cho', '.chopro', '.crd', '.pro', '.txt', '.pdf'];
const PARALLELT = 4;

function lesBytes(fil: File): Promise<ArrayBuffer> {
  if (typeof fil.arrayBuffer === 'function') return fil.arrayBuffer();
  return new Promise((ok, feil) => {
    const leser = new FileReader();
    leser.onload = () => ok(leser.result as ArrayBuffer);
    leser.onerror = () => feil(leser.error);
    leser.readAsArrayBuffer(fil);
  });
}

function dekod(bytes: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

const endelse = (navn: string): string => {
  const i = navn.lastIndexOf('.');
  return i < 0 ? '' : navn.slice(i).toLowerCase();
};

type FilUtfall = { felter: SangFelter } | { hoppet: string };

/**
 * Filens toneart i kanonisk form. Ugyldig verdi sendes aldri videre, for serveren ville avvist hele raden.
 * Bare tall-filer får filnavnet som reserve og kan mangle toneart.
 */
function finnToneart(felter: SangFelter, filnavn: string): Pick<Sang, 'toneartKilde'> & { felter: SangFelter; mangler: boolean } {
  const tekst = felter.tekst_og_akkorder;
  const egen = normaliserToneart(felter.toneart);
  if (!tekst || !harTallakkorder(tekst)) return { felter: { ...felter, toneart: egen }, toneartKilde: null, mangler: false };
  if (egen) return { felter: { ...felter, toneart: egen }, toneartKilde: 'fil', mangler: false };
  const fraNavn = tonartFraFilnavn(filnavn);
  if (fraNavn) return { felter: { ...felter, toneart: fraNavn }, toneartKilde: 'filnavn', mangler: false };
  return { felter: { ...felter, toneart: null }, toneartKilde: null, mangler: true };
}

async function lesFil(fil: File, maksStorrelse: number): Promise<FilUtfall> {
  const ende = endelse(fil.name);
  if (!ENDELSER.includes(ende)) return { hoppet: `${fil.name}: filtypen støttes ikke` };
  if (fil.size > maksStorrelse) return { hoppet: `${fil.name}: filen er for stor` };
  try {
    if (ende === '.pdf') {
      const { lesPdfSang } = await import('./lesPdfSang');
      return { felter: (await lesPdfSang(fil)).felter };
    }
    const tekst = dekod(await lesBytes(fil));
    return { felter: ende === '.txt' ? tolkTekstfil(tekst, fil.name) : tolkChordPro(tekst, fil.name) };
  } catch (err) {
    const melding = err instanceof Error ? err.message : '';
    if (melding.startsWith(fil.name)) return { hoppet: melding };
    console.error('lesSangfiler: lesing feilet', err);
    return { hoppet: `${fil.name}: kunne ikke leses` };
  }
}

export async function lesSangfiler(filer: File[], valg: LesSangfilerValg = {}): Promise<LesSangfilerResultat> {
  const { maksFilstorrelse = 5 * 1024 * 1024, maksFiler = 1000 } = valg;
  if (filer.length > maksFiler) return { feil: [{ melding: `For mange filer (${filer.length}). Maks ${maksFiler} om gangen.` }] };

  const utfall: FilUtfall[] = new Array(filer.length);
  let neste = 0;
  const arbeider = async () => {
    while (neste < filer.length) {
      const i = neste++;
      utfall[i] = await lesFil(filer[i], maksFilstorrelse);
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLELT, filer.length) }, arbeider));

  const filadvarsler: string[] = [];
  const sanger: Sang[] = [];
  utfall.forEach((u, i) => {
    if ('hoppet' in u) { filadvarsler.push(u.hoppet); return; }
    const filnavn = filer[i].name;
    const t = finnToneart(u.felter, filnavn);
    sanger.push({
      filnavn,
      felter: t.felter,
      mangler: { toneart: t.mangler, artist: !t.felter.artist || t.felter.artist.trim() === '' },
      toneartKilde: t.toneartKilde,
    });
  });
  if (sanger.length === 0) {
    return { feil: [{ melding: filadvarsler.length ? `Ingen filer kunne leses. ${filadvarsler.join('; ')}` : 'Ingen filer å lese.' }] };
  }
  return { sanger, filadvarsler };
}

/**
 * Bygger CSV for importen. Teksten lagres slik filen har den, også tall-akkorder.
 * Svar (steg 2 eller rettelser på raden) går foran det filen sier om toneart og artist. Ugyldig toneart i svaret brukes ikke.
 */
export function sangerTilCsv(sanger: Sang[], svar: SangSvar): SangerCsv {
  const rader: (string | null)[][] = [];
  for (const { felter: f, filnavn } of sanger) {
    const s = svar[filnavn];
    const artist = s?.artist !== undefined ? s.artist.trim() || null : f.artist;
    const toneart = normaliserToneart(s?.toneart) ?? f.toneart;
    rader.push([f.tittel, artist, f.album, f.utgivelsesaar, f.lengde, toneart, f.bpm, f.takt, null, null, f.tekst_og_akkorder, null, null]);
  }
  return { csv: lagCsv(OVERSKRIFTER, rader), filadvarsler: [] };
}
